import React, { Component } from "react";
import agent from "../agent";
import { connect } from "react-redux";

import { AsideDefault } from "../widgets/components/aside/AsideDefault";
import { Footer } from "../widgets/components/Footer";
import { HeaderWrapper } from "../widgets/components/header/HeaderWrapper";
import { Toolbar } from "../widgets/components/toolbar/Toolbar";
import { ScrollTop } from "../widgets/components/ScrollTop";
import { Content } from "../widgets/components/Content";

import { ListLoading } from "../widgets/helpers/ListLoading";

import { START_LOADING, STOP_LOADING } from "../constants/actionTypes";

const mapStateToProps = (state) => ({
  currentUser: state.common.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
  startLoading: () => dispatch({ type: START_LOADING }),
  stopLoading: () => dispatch({ type: STOP_LOADING }),
});

class AdminVideoAnalytics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      analytics: null,
      videoId: "",
      startDate: "",
      endDate: "",
      isLoading: false,
      error: null,
      selectedTimeRange: "7days",
      connectionStatus: null,
      connectionLoading: false,
    };
  }

  componentDidMount() {
    // Set default date range to last 7 days
    this.setTimeRange("7days");
    // Load Vimeo connection status
    this.loadConnectionStatus();

    // Check for OAuth callback parameters
    this.handleOAuthCallback();
  }

  handleOAuthCallback = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get("connected");
    const error = urlParams.get("error");
    const account = urlParams.get("account");

    if (connected === "1" && account) {
      this.setState({
        error: null,
        connectionStatus: {
          connected: true,
          status: "connected",
          message: `Successfully connected to ${decodeURIComponent(account)}`,
          account_name: decodeURIComponent(account),
        },
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      this.setState({
        error: `OAuth Error: ${decodeURIComponent(error)}`,
        connectionStatus: { connected: false, status: "error" },
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  loadConnectionStatus = async () => {
    try {
      this.setState({ connectionLoading: true });
      const response = await agent.VimeoOAuth.getStatus();

      if (response.success) {
        this.setState({ connectionStatus: response.data });
      }
    } catch (error) {
      console.error("Error loading connection status:", error);
    } finally {
      this.setState({ connectionLoading: false });
    }
  };

  connectVimeo = async () => {
    try {
      this.setState({ connectionLoading: true, error: null });
      const response = await agent.VimeoOAuth.connect();

      if (response.success && response.data.authorization_url) {
        // Redirect to Vimeo OAuth
        window.location.href = response.data.authorization_url;
      } else {
        throw new Error("Failed to get authorization URL");
      }
    } catch (error) {
      console.error("Error connecting to Vimeo:", error);
      this.setState({
        error: error.message || "Failed to initiate Vimeo connection",
        connectionLoading: false,
      });
    }
  };

  disconnectVimeo = async () => {
    try {
      this.setState({ connectionLoading: true, error: null });
      const response = await agent.VimeoOAuth.disconnect();

      if (response.success) {
        this.setState({
          connectionStatus: { connected: false, status: "not_connected" },
          analytics: null, // Clear analytics when disconnecting
        });
      } else {
        throw new Error("Failed to disconnect");
      }
    } catch (error) {
      console.error("Error disconnecting from Vimeo:", error);
      this.setState({
        error: error.message || "Failed to disconnect from Vimeo",
      });
    } finally {
      this.setState({ connectionLoading: false });
    }
  };

  testConnection = async () => {
    try {
      this.setState({ connectionLoading: true, error: null });
      const response = await agent.VimeoOAuth.test();

      if (response.success && response.data.connected) {
        this.setState({
          connectionStatus: {
            ...this.state.connectionStatus,
            status: "connected",
            message: "Connection test successful",
          },
        });
      } else {
        this.setState({
          connectionStatus: {
            ...this.state.connectionStatus,
            status: "error",
            message: response.data.message || "Connection test failed",
          },
        });
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      this.setState({
        connectionStatus: {
          ...this.state.connectionStatus,
          status: "error",
          message: error.message || "Connection test failed",
        },
      });
    } finally {
      this.setState({ connectionLoading: false });
    }
  };

  setTimeRange = (range) => {
    const now = new Date();
    let startDate, endDate;

    switch (range) {
      case "24hours":
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case "7days":
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case "30days":
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case "90days":
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      default:
        return;
    }

    this.setState({
      selectedTimeRange: range,
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    });
  };

  loadAnalytics = async () => {
    if (!this.state.videoId) {
      this.setState({ error: "Please enter a Video ID" });
      return;
    }

    try {
      this.setState({ isLoading: true, error: null });
      this.props.startLoading();

      const response = await agent.Video.getAnalytics(
        this.state.videoId,
        this.state.startDate,
        this.state.endDate
      );

      if (response.success) {
        this.setState({ analytics: response.data });
      } else {
        throw new Error(response.error || "Failed to load analytics");
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
      this.setState({ error: error.message || "Failed to load analytics" });
    } finally {
      this.setState({ isLoading: false });
      this.props.stopLoading();
    }
  };

  handleInputChange = (field, value) => {
    this.setState({ [field]: value });
  };

  formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  render() {
    const {
      analytics,
      videoId,
      startDate,
      endDate,
      isLoading,
      error,
      selectedTimeRange,
    } = this.state;

    return (
      <>
        <div className="d-flex flex-column flex-root">
          <div className="page d-flex flex-row flex-column-fluid">
            <AsideDefault />
            <div
              className="wrapper d-flex flex-column flex-row-fluid"
              id="kt_wrapper"
            >
              <HeaderWrapper
                title="Video Analytics"
                currentUser={this.props.currentUser}
              />
              <Toolbar />
              <div
                id="kt_content"
                className="content d-flex flex-column flex-column-fluid"
              >
                <Content>
                  <div
                    className="subheader pb-2 pb-lg-4 subheader-transparent"
                    id="kt_subheader"
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap">
                      <div className="d-flex align-items-center flex-wrap mr-2">
                        <h5 className="text-dark font-weight-bold mt-2 mb-2">
                          Vimeo Video Analytics
                        </h5>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column">
                    {/* Vimeo Connection Status */}
                    <div className="card card-custom mb-4">
                      <div className="card-header">
                        <div className="card-title">
                          <h3 className="card-label">
                            <i className="bi bi-camera-video-fill me-2"></i>
                            Vimeo Account Connection
                          </h3>
                        </div>
                      </div>
                      <div className="card-body">
                        {this.state.connectionLoading ? (
                          <div className="d-flex align-items-center">
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></div>
                            <span>Checking connection...</span>
                          </div>
                        ) : this.state.connectionStatus ? (
                          <div className="row align-items-center">
                            <div className="col-md-8">
                              <div className="d-flex align-items-center">
                                {this.state.connectionStatus.connected ? (
                                  <>
                                    <div className="symbol symbol-40px me-3">
                                      <div className="symbol-label bg-success">
                                        <i className="bi bi-check-circle-fill text-white"></i>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="fw-bold text-dark">
                                        Connected to{" "}
                                        {this.state.connectionStatus
                                          .account_name || "Vimeo"}
                                      </div>
                                      <div className="text-muted small">
                                        {this.state.connectionStatus.message}
                                        {this.state.connectionStatus
                                          .connected_at && (
                                          <span className="ms-2">
                                            • Connected{" "}
                                            {new Date(
                                              this.state.connectionStatus.connected_at
                                            ).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="symbol symbol-40px me-3">
                                      <div className="symbol-label bg-warning">
                                        <i className="bi bi-exclamation-triangle-fill text-white"></i>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="fw-bold text-dark">
                                        Not Connected
                                      </div>
                                      <div className="text-muted small">
                                        {this.state.connectionStatus.message ||
                                          "Connect your Vimeo account to access private videos"}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="col-md-4 text-end">
                              {this.state.connectionStatus.connected ? (
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={this.testConnection}
                                    disabled={this.state.connectionLoading}
                                  >
                                    <i className="bi bi-arrow-clockwise me-1"></i>
                                    Test
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline btn-outline-danger"
                                    onClick={this.disconnectVimeo}
                                    disabled={this.state.connectionLoading}
                                  >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Disconnect
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="btn btn-primary"
                                  onClick={this.connectVimeo}
                                  disabled={this.state.connectionLoading}
                                >
                                  <i className="bi bi-link-45deg me-1"></i>
                                  Connect Vimeo Account
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-3">
                            <div className="text-muted">
                              Loading connection status...
                            </div>
                          </div>
                        )}

                        {!this.state.connectionStatus?.connected && (
                          <div className="alert alert-info mt-3">
                            <p className="mb-2">
                              Connecting Vimeo Enterprise account allows secure
                              access to:
                            </p>
                            <ul className="mb-0">
                              <li>Private videos and live streams</li>
                              <li>Advanced analytics and viewer data</li>
                              <li>Enhanced security controls</li>
                              <li>Real-time streaming capabilities</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Analytics Controls */}
                    <div className="card card-custom mb-4">
                      <div className="card-header">
                        <div className="card-title">
                          <h3 className="card-label">Analytics Filters</h3>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-4">
                            <label className="form-label">Video ID</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter Vimeo Video ID"
                              value={videoId}
                              onChange={(e) =>
                                this.handleInputChange(
                                  "videoId",
                                  e.target.value
                                )
                              }
                            />
                            <div className="form-text">
                              Enter the Vimeo video ID (e.g., 123456789) or full
                              URL
                            </div>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Time Range</label>
                            <select
                              className="form-select"
                              value={selectedTimeRange}
                              onChange={(e) =>
                                this.setTimeRange(e.target.value)
                              }
                            >
                              <option value="24hours">Last 24 Hours</option>
                              <option value="7days">Last 7 Days</option>
                              <option value="30days">Last 30 Days</option>
                              <option value="90days">Last 90 Days</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Start Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={startDate}
                              onChange={(e) =>
                                this.handleInputChange(
                                  "startDate",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">End Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={endDate}
                              onChange={(e) =>
                                this.handleInputChange(
                                  "endDate",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div className="col-md-1">
                            <label className="form-label">&nbsp;</label>
                            <button
                              className="btn btn-primary d-block"
                              onClick={this.loadAnalytics}
                              disabled={isLoading}
                            >
                              {isLoading ? "Loading..." : "Load"}
                            </button>
                          </div>
                        </div>

                        {error && (
                          <div className="alert alert-danger mt-3">{error}</div>
                        )}
                      </div>
                    </div>

                    {/* Analytics Results */}
                    {analytics && (
                      <>
                        {/* Summary Cards */}
                        <div className="row mb-4">
                          <div className="col-md-3">
                            <div className="card card-custom bg-primary">
                              <div className="card-body text-white">
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-eye-fill fs-2x me-3"></i>
                                  <div>
                                    <div className="fs-2 fw-bold">
                                      {this.formatNumber(analytics.total_views)}
                                    </div>
                                    <div className="fs-6">Total Views</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card card-custom bg-success">
                              <div className="card-body text-white">
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-people-fill fs-2x me-3"></i>
                                  <div>
                                    <div className="fs-2 fw-bold">
                                      {this.formatNumber(
                                        analytics.unique_viewers
                                      )}
                                    </div>
                                    <div className="fs-6">Unique Viewers</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card card-custom bg-info">
                              <div className="card-body text-white">
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-clock-fill fs-2x me-3"></i>
                                  <div>
                                    <div className="fs-2 fw-bold">
                                      {this.formatDuration(
                                        analytics.average_session_duration
                                      )}
                                    </div>
                                    <div className="fs-6">Avg Watch Time</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="card card-custom bg-warning">
                              <div className="card-body text-white">
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-bar-chart-fill fs-2x me-3"></i>
                                  <div>
                                    <div className="fs-2 fw-bold">
                                      {analytics.unique_viewers > 0
                                        ? (
                                            analytics.total_views /
                                            analytics.unique_viewers
                                          ).toFixed(1)
                                        : "0"}
                                    </div>
                                    <div className="fs-6">Views per User</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Views by Date Chart */}
                        <div className="card card-custom mb-4">
                          <div className="card-header">
                            <div className="card-title">
                              <h3 className="card-label">Views by Date</h3>
                            </div>
                          </div>
                          <div className="card-body">
                            {analytics.views_by_date &&
                            Object.keys(analytics.views_by_date).length > 0 ? (
                              <div className="table-responsive">
                                <table className="table table-striped">
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Views</th>
                                      <th>Percentage</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(analytics.views_by_date)
                                      .sort(
                                        ([a], [b]) => new Date(b) - new Date(a)
                                      )
                                      .map(([date, views]) => (
                                        <tr key={date}>
                                          <td>
                                            {new Date(
                                              date
                                            ).toLocaleDateString()}
                                          </td>
                                          <td>{this.formatNumber(views)}</td>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <div
                                                className="progress me-2"
                                                style={{
                                                  width: "100px",
                                                  height: "6px",
                                                }}
                                              >
                                                <div
                                                  className="progress-bar bg-primary"
                                                  style={{
                                                    width: `${
                                                      (views /
                                                        analytics.total_views) *
                                                      100
                                                    }%`,
                                                  }}
                                                ></div>
                                              </div>
                                              <span className="text-muted">
                                                {(
                                                  (views /
                                                    analytics.total_views) *
                                                  100
                                                ).toFixed(1)}
                                                %
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <p className="text-muted">
                                  No viewing data available for this time
                                  period.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Top Viewers */}
                        <div className="card card-custom">
                          <div className="card-header">
                            <div className="card-title">
                              <h3 className="card-label">Top Viewers</h3>
                            </div>
                          </div>
                          <div className="card-body">
                            {analytics.top_viewers &&
                            Object.keys(analytics.top_viewers).length > 0 ? (
                              <div className="table-responsive">
                                <table className="table table-striped">
                                  <thead>
                                    <tr>
                                      <th>User ID</th>
                                      <th>Total Views</th>
                                      <th>Percentage of Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(analytics.top_viewers)
                                      .sort(([, a], [, b]) => b - a)
                                      .slice(0, 10)
                                      .map(([userId, views]) => (
                                        <tr key={userId}>
                                          <td>User #{userId}</td>
                                          <td>{this.formatNumber(views)}</td>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <div
                                                className="progress me-2"
                                                style={{
                                                  width: "100px",
                                                  height: "6px",
                                                }}
                                              >
                                                <div
                                                  className="progress-bar bg-success"
                                                  style={{
                                                    width: `${
                                                      (views /
                                                        analytics.total_views) *
                                                      100
                                                    }%`,
                                                  }}
                                                ></div>
                                              </div>
                                              <span className="text-muted">
                                                {(
                                                  (views /
                                                    analytics.total_views) *
                                                  100
                                                ).toFixed(1)}
                                                %
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <p className="text-muted">
                                  No viewer data available for this time period.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {isLoading && <ListLoading />}
                  </div>
                </Content>
              </div>
              <Footer />
            </div>
          </div>
        </div>
        <ScrollTop />
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AdminVideoAnalytics);
